import {isMediaString} from '@wandb/weave/common/types/media';

export type CollapsibleMetricTypes = {
  types: string[];
  /**
   * The most recently logged type across the queried runs, from
   * `sectionAwareHistoryKeys` and `metricSearch`. Null when the store can't
   * resolve it: unknown, not number.
   */
  latestType?: string | null;
};

const isChartType = (type: string) => type === 'number' || type === 'histogram';

/**
 * Returns a single type for a metric, to help panel generation pick a line plot
 * or a media panel, and to help media pickers pick a card type.
 *
 * Uses `latestType` when it names a media type, since media has to choose one
 * card type per panel and the latest one is what the user logs now. Otherwise
 * takes the first type in `types` we render, media before charts. Charts
 * ignore `latestType`: having logged a number or histogram at all is enough,
 * so a key whose latest value is a number still gets a media panel if it ever
 * logged media. The backend sorts `types` and documents the order as
 * meaningless, so among several media types without a `latestType` the choice
 * is still arbitrary.
 *
 * Falls back to the first entry when no type is one we render, and returns
 * null when there is nothing to go on, so the key generates no panel.
 */
export function collapseMetricTypes(
  metric: CollapsibleMetricTypes
): string | null {
  const {types, latestType} = metric;
  if (latestType != null && isMediaString(latestType)) {
    return latestType;
  }
  return (
    types.find(isMediaString) ?? types.find(isChartType) ?? types[0] ?? null
  );
}
